import type {ClipboardEvent, KeyboardEvent, ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {CodeEdit, VerificationCodeInputProps} from './verification-code-input.types';
import {VERIFICATION_CODE_LENGTH} from './verification-code-input.types';
import styles from './verification-code-input.module.scss';
import {getCodeAfterBackspace, getCodeAfterTyping, toCodeDigits} from './verification-code-input-library';

/**
 * Six single-character boxes that behave as one field: typing advances, backspace retreats, arrows and
 * paste work across the whole code. The value is owned by the caller as a plain string, so the boxes are
 * only a presentation of it and the form never has to reassemble anything.
 */
export function VerificationCodeInput({value, onChange, onSubmit, hasError = false}: VerificationCodeInputProps): ReactElement {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const focusBox = (index: number): void => {
    if (index < 0 || index >= VERIFICATION_CODE_LENGTH) {
      return;
    }
    const box = inputRefs.current[index];
    box?.focus();
    box?.select();
  };

  const applyEdit = (edit: CodeEdit): void => {
    if (edit.code !== value) {
      onChange(edit.code);
    }
    focusBox(edit.focusIndex);
  };

  const onBoxChange = (index: number, boxValue: string): void => {
    applyEdit(getCodeAfterTyping(value, index, boxValue));
  };

  const onBoxKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      applyEdit(getCodeAfterBackspace(value, index));
      return;
    }

    if (event.key === 'ArrowLeft') {
      focusBox(index - 1);
    }

    if (event.key === 'ArrowRight') {
      focusBox(index + 1);
    }

    if (event.key === 'Enter') {
      onSubmit();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const pasted = toCodeDigits(event.clipboardData.getData('text')).slice(0, VERIFICATION_CODE_LENGTH);
    if (pasted === '') {
      return;
    }
    applyEdit({code: pasted, focusIndex: Math.min(pasted.length, VERIFICATION_CODE_LENGTH - 1)});
  };

  const isBoxFilled = (index: number): boolean => {
    return (value[index] ?? '').trim() !== '';
  };

  /** A box holding a digit stays brand-bordered after focus moves on; an error outranks it. */
  const getBoxClasses = (index: number): string => {
    if (hasError) {
      return `${styles.codeBox} ${styles.codeBoxError}`;
    }
    return isBoxFilled(index) ? `${styles.codeBox} ${styles.codeBoxFilled}` : styles.codeBox;
  };

  const getBoxes = (): ReactElement[] => {
    return [...Array(VERIFICATION_CODE_LENGTH).keys()].map((index) => {
      return (
        <input
          aria-label={`${window.i18next.t('pmwjs_two_factor_authentication_code')} ${index + 1}`}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          className={getBoxClasses(index)}
          inputMode="numeric"
          key={index}
          onChange={(event) => {
            onBoxChange(index, event.target.value);
          }}
          onFocus={(event) => {
            event.target.select();
          }}
          onKeyDown={(event) => {
            onBoxKeyDown(index, event);
          }}
          onPaste={onPaste}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          value={value[index] ?? ''}
        />
      );
    });
  };

  return <div className={styles.codeBoxRow}>{getBoxes()}</div>;
}
